// Keyshawn summonable drawer Redux slice (F8 / MVP-2 foundation).
//
// Owns the lifecycle of the new student-summoned Keyshawn drawer:
//   - open/close (page-shift drawer in Dashboard.jsx)
//   - activeStepType + activeStepEntityId (which student surface is in
//     scope when the drawer opens - set by per-surface useEffects in
//     MissionBoard / KeyshawnDefineChat / ReflectionDrawer)
//   - messages (kid <-> Keyshawn conversation, session-scoped)
//   - disclosureAcknowledged (mirror of localStorage flag for the first
//     scored-step disclosure toast, F9 wires the UI)
//   - currentSummonSessionId (uuid generated on each new drawer open;
//     groups coaching_interactions rows on the backend for per-session
//     analytics + AI-use backfill)
//
// NOT persisted (intentionally absent from redux-persist whitelist in
// src/store/index.js). The drawer is a session-only surface - every
// drawer-open is a fresh summon. Persisting would surface stale
// conversations across browser sessions, which would conflict with the
// per-step reset semantics owned by step-boundary changes.
//
// Per plan v8 §3 F8 + Architecture Decisions LOCKED.
import { createSlice } from "@reduxjs/toolkit";

import { GUIDANCE_COPY } from "../../lib/keyshawn/keyshawnGuidanceContext";

const GUIDANCE_INTERRUPTED_TEXT = GUIDANCE_COPY.interrupted;

const initialState = {
  isOpen: false,
  // 'planning' | 'task_claim' | 'task_define' | 'reflection' | null
  activeStepType: null,
  activeStepEntityId: null,
  messages: [],
  // Reserved for v4.5 SSE - sync POST in MVP.
  isStreaming: false,
  disclosureAcknowledged: false,
  // uuid set by useKeyshawnHotkey on summon. Backend logs it on every
  // coaching_interactions row so analytics can group by session.
  currentSummonSessionId: null,
  // FE-G1 guidance v2. `key` is null unless a v2 surface owns the drawer,
  // which can only happen with the guidance flag on and an eligible student.
  // Everything here is serializable identity and request metadata; drafts,
  // AbortControllers and DOM refs live with the mounted caller.
  guidance: {
    key: null,
    generation: 0,
    descriptor: null,
    pendingRequestId: null,
    // True while a surface that had opted into v2 is still open but has lost
    // its eligibility or identity. The drawer keeps its v2 presentation with
    // the composer disabled; nothing legacy may register, send or hydrate.
    unavailable: false,
  },
};

/** The guidance record, tolerating a hand-built test store without one. */
const guidanceOf = (state) => {
  if (!state.guidance) {
    state.guidance = {
      key: null,
      generation: 0,
      descriptor: null,
      pendingRequestId: null,
      unavailable: false,
    };
  }
  return state.guidance;
};

/** A legacy step dispatch must not act while v2 owns or is withdrawn. */
const guidanceHolds = (state) =>
  Boolean(
    state.guidance &&
      (state.guidance.key !== null || state.guidance.unavailable === true),
  );

/** A request action belongs to the live identity only on an exact match. */
const isLiveIdentity = (guidance, payload) =>
  guidance.key !== null &&
  payload?.key === guidance.key &&
  payload?.generation === guidance.generation;

const keyshawnSlice = createSlice({
  name: "keyshawn",
  initialState,
  reducers: {
    open(state, action) {
      state.isOpen = true;
      // Action payload optionally carries the summon_session_id uuid.
      if (action.payload?.summonSessionId) {
        state.currentSummonSessionId = action.payload.summonSessionId;
      }
    },
    close(state) {
      state.isOpen = false;
      // Clear summon session id on close so the next open generates a
      // fresh uuid. Messages are cleared on step-boundary change, not
      // on close, so a kid who closes/reopens within the same step sees
      // their conversation (re-hydration runs via useKeyshawnHydrate).
      state.currentSummonSessionId = null;
      // FE-G1: closing a v2 drawer abandons its pending request. Completed
      // messages stay, so reopening the same context shows them, and the
      // unanswered question is marked interrupted with a deliberate retry
      // (never an automatic resend). Legacy close is untouched: this branch
      // needs a live guidance key.
      if (state.guidance && state.guidance.key !== null) {
        if (state.guidance.pendingRequestId !== null) {
          const last = state.messages[state.messages.length - 1];
          if (last && last.sender === "student") {
            state.messages.push({
              sender: "keyshawn",
              text: GUIDANCE_INTERRUPTED_TEXT,
              createdAt: Date.now(),
              guidance: {
                kind: "failed",
                retryText: last.text,
                contextStatus: "unavailable",
                contextLabel: null,
                sources: [],
                actions: [],
                fallback: false,
              },
            });
          }
        }
        state.guidance.pendingRequestId = null;
        state.isStreaming = false;
      }
    },
    setActiveStepType(state, action) {
      // FE-G1: while a guidance v2 surface owns the drawer, identity comes
      // only from setGuidanceSurface. A legacy dispatch (for example a
      // passive-effect cleanup that runs after the v2 layout-effect
      // registration) must not clobber it, nor may one re-open a legacy
      // step under a withdrawn v2 surface. Never true with the flag off.
      if (guidanceHolds(state)) return;
      const { stepType, entityId } = action.payload || {};
      // Step boundary check: clear messages + summon session when the
      // step or entity changes. The drawer is per-step scoped - moving
      // from task A's why to task B's why is a fresh thread.
      const stepChanged =
        state.activeStepType !== stepType ||
        state.activeStepEntityId !== entityId;
      state.activeStepType = stepType ?? null;
      state.activeStepEntityId = entityId ?? null;
      if (stepChanged) {
        state.messages = [];
        state.currentSummonSessionId = null;
      }
    },
    clearActiveStepType(state) {
      if (guidanceHolds(state)) return;
      state.activeStepType = null;
      state.activeStepEntityId = null;
      state.messages = [];
      state.currentSummonSessionId = null;
    },
    addMessage(state, action) {
      state.messages.push(action.payload);
    },
    setMessages(state, action) {
      state.messages = Array.isArray(action.payload) ? action.payload : [];
    },
    setStreaming(state, action) {
      state.isStreaming = !!action.payload;
    },
    acknowledgeDisclosure(state) {
      state.disclosureAcknowledged = true;
    },
    // FE-G1 guidance v2. Published by CompositionRefProvider whenever the
    // active v2 surface (or its identity key) changes. A new key starts a new
    // generation and drops the old conversation and any pending request.
    setGuidanceSurface(state, action) {
      const { descriptor, key } = action.payload || {};
      if (!descriptor || typeof key !== "string") return;
      const guidance = guidanceOf(state);
      if (key !== guidance.key) {
        guidance.generation += 1;
        guidance.pendingRequestId = null;
        state.messages = [];
        state.isStreaming = false;
        state.currentSummonSessionId = null;
      }
      guidance.key = key;
      guidance.descriptor = descriptor;
      guidance.unavailable = false;
      state.activeStepType = descriptor.stepType ?? null;
      state.activeStepEntityId = descriptor.stepEntityId ?? null;
    },
    // FE-G1: the front surface opted into v2 and is still open, but lost its
    // eligibility or identity. Leave v2 (no key, no pending request, an empty
    // conversation, a new generation so any late reply drops) WITHOUT
    // exposing anything beneath it and WITHOUT legacy fallback. The step type
    // is kept on purpose, so the drawer's step-closure cascade does not fire
    // while the surface is still on screen.
    setGuidanceUnavailable(state) {
      const guidance = guidanceOf(state);
      guidance.key = null;
      guidance.descriptor = null;
      guidance.pendingRequestId = null;
      guidance.generation += 1;
      guidance.unavailable = true;
      state.messages = [];
      state.isStreaming = false;
    },
    clearGuidanceSurface(state) {
      const guidance = guidanceOf(state);
      guidance.key = null;
      guidance.descriptor = null;
      guidance.pendingRequestId = null;
      guidance.unavailable = false;
      guidance.generation += 1;
      state.messages = [];
      state.activeStepType = null;
      state.activeStepEntityId = null;
      state.currentSummonSessionId = null;
      state.isStreaming = false;
    },
    guidanceRequestStarted(state, action) {
      const guidance = guidanceOf(state);
      const { requestId, studentMessage, createdAt } = action.payload || {};
      if (!isLiveIdentity(guidance, action.payload) || !requestId) return;
      state.messages.push({
        sender: "student",
        text: studentMessage,
        createdAt: createdAt ?? Date.now(),
      });
      guidance.pendingRequestId = requestId;
      state.isStreaming = true;
    },
    // Applies ONLY to the live key + generation AND the request still pending.
    // No pending record (closed, cleared, superseded) means drop: a late
    // settle can never create state.
    guidanceRequestSettled(state, action) {
      const guidance = guidanceOf(state);
      const { requestId, message } = action.payload || {};
      if (!isLiveIdentity(guidance, action.payload)) return;
      if (!requestId || guidance.pendingRequestId !== requestId) return;
      if (message) state.messages.push(message);
      guidance.pendingRequestId = null;
      state.isStreaming = false;
    },
    guidanceRequestAbandoned(state, action) {
      const guidance = guidanceOf(state);
      const requestId = action.payload?.requestId;
      if (!requestId || guidance.pendingRequestId !== requestId) return;
      guidance.pendingRequestId = null;
      state.isStreaming = false;
    },
    // G.2c.1: the openPlanningChat scaffold action was REMOVED. The
    // plan-step "ask keyshawn" button now dispatches the real
    // open({ summonSessionId }) summon (same action as the global
    // useKeyshawnHotkey ⇧⇧ gesture) - one entry path, one drawer.
    // G.2d builds on open(), not a parallel planning-chat action.
  },
});

export const {
  open,
  close,
  setActiveStepType,
  clearActiveStepType,
  addMessage,
  setMessages,
  setStreaming,
  acknowledgeDisclosure,
  setGuidanceSurface,
  setGuidanceUnavailable,
  clearGuidanceSurface,
  guidanceRequestStarted,
  guidanceRequestSettled,
  guidanceRequestAbandoned,
} = keyshawnSlice.actions;

// Selectors (named-export pattern so consumers don't import the whole
// state shape inline; mirrors the planTasks slice convention).
export const selectIsOpen = (state) => state.keyshawn.isOpen;
export const selectActiveStepType = (state) => state.keyshawn.activeStepType;
export const selectActiveStepEntityId = (state) =>
  state.keyshawn.activeStepEntityId;
export const selectMessages = (state) => state.keyshawn.messages;
export const selectIsStreaming = (state) => state.keyshawn.isStreaming;
export const selectDisclosureAcknowledged = (state) =>
  state.keyshawn.disclosureAcknowledged;
export const selectCurrentSummonSessionId = (state) =>
  state.keyshawn.currentSummonSessionId;

// FE-G1 guidance v2 selectors. `?.` because several suites build the
// keyshawn slice by hand without a `guidance` record.
export const selectGuidance = (state) => state.keyshawn?.guidance ?? null;
export const selectGuidanceActive = (state) =>
  state.keyshawn?.guidance?.key != null;
export const selectGuidancePending = (state) =>
  state.keyshawn?.guidance?.pendingRequestId != null;
export const selectGuidanceUnavailable = (state) =>
  state.keyshawn?.guidance?.unavailable === true;

export default keyshawnSlice.reducer;
